import { DataFlowExpression } from './dataflowexpression';
import {
    AzureSqlDWTableDataset,
    Constants,
    DataFlowParameterConfig,
    DelimitedTextDataset,
    IAttribute,
    IDataflowSource,
    IModel,
    IValidationContext,
    IViolation,
    LinkedService,
    ModelConstants,
    OwnedSet,
    Parameter,
    ReadBehavior,
    SelfHostedIntegrationRuntime
} from 'shell-models-lib';
import { Metamodel } from '../base/model';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';

import { Dataset } from 'app/model/dataset/dataset';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { ISchemaBasedTransformation } from 'app/model/dataflow/ISchemaBasedTransformation';
import { Owned } from '../base/named';
import { DataFlowTypeConvertor } from './data-flow-type-convertor';
import { format, sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import {
    BooleanDataFlowType,
    IntegerDataFlowType,
    LongDataFlowType,
    NumberDataFlowType,
    StringDataFlowType,
    TimeStampDataFlowType
} from './dataflow-datatype';
import { BaseSchemaGeneratorTransformation } from './base-schema-generator-transformation';
import { AzureSqlTableDataset } from 'app/model/dataset/azure-sql-table.dataset';
import { ColumnReference } from '../dataset/columnReference';
import { IDataFlowDefaultTypeSettings } from './IDataflow-default-format';
import { mdfConnectionTypeValidator, mdfFormatValidator, sourceSinkLinkedServiceValidator, transformationDatasetValidator } from './transformation-dataset-validator';
import { ISupportsQuery } from './ISupportsQuery';
import { isTruthy } from './dataflow-expression-utilities';
import { Transformation } from './transformation';
import { AzureCosmosDbV2Dataset } from '../dataset/azure-cosmos-db.v2.dataset';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataset-settings/dataflow-dataset-settings';
import { ExcelDataset } from '../dataset/excel.dataset';
import { IComplexKeyValPair } from 'app/interfaces/IComplexKeyValPair';
import { XmlDataset } from '../dataset/xml.dataset';
import { workspaceDatabaseValidator, workspaceTableValidator } from './workspace-database-validators';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { cosmosSystemColumns } from 'app/shared/utils/column-type.util';
import { isSourceTypeEq, updateDatasetSettingsCache } from 'app/model/dataflow/dataflow-utils';
import { getDataFlowBaseArguments } from 'app/shared/components/parameterizable/parameterizable-dataflow-utilities';
import { SapOdpPartitionPredicate } from 'app/model/dataflow/sap-odp-partition-predicate';
import { SapOdpDataset } from 'app/model/dataset/sap-odp.dataset';
import { ReadMode, SapOdpSettings } from 'app/model/dataflow/dataset-settings/sap-odp-settings';
import { SapOdpLinkedService } from 'app/model/linkedservice/sap-odp.linkedservice';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { SapSelectionOption } from 'app/model/pipeline/copysource/sap-odp.copysource';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { propertyIsNullOrEmpty } from 'app/model/adapters/linkedservices/linked-service-adapter.util';
import { EventHubsSettings } from './dataset-settings/event-hubs-settings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export enum CompletionAction {
    NoAction,
    DeleteSource,
    MoveFile
}

export enum DocumentForm {
    singleDocument = 'singleDocument',
    documentPerLine = 'documentPerLine',
    arrayOfDocuments = 'arrayOfDocuments'
}

export enum IsolationLevel {
    None = 'NONE',
    ReadCommitted = 'READ_COMMITTED',
    ReadUncommitted = 'READ_UNCOMMITTED',
    RepeatableRead = 'REPEATABLE_READ',
    Serializable = 'SERIALIZABLE'
}

export enum SQLSourceOptions {
    Table = 'table',
    Query = 'query',
    Procedure = 'procedure'
}

@Metamodel.model({
    name: ModelConstants.dataFlowSource,
    display: [stringResources.DataFlowSourceLabel, stringResources.DataFlowSourcesLabel],
    referable: true,
    constraints: {
        name: {
            required: true,
            pattern: new RegExp('^[^-\\s][^\\\\/:*?"<>|]{1,140}$'),
            uniqueModelName: true
        }
    },
    validator: DataFlowSource.validateDataFlowSource
})
export class DataFlowSource extends BaseSchemaGeneratorTransformation implements ISchemaBasedTransformation, ISupportsQuery, IDataflowSource {
    public static readonly MODEL: IModel;

    public static readonly STAGING_LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly MDF_CONNECTION_TYPE: IAttribute;
    public static readonly DATASET_SETTINGS: IAttribute;

    @Metamodel.attribute({
        display: stringResources.SourceDataset,
        constraint: {
            validator: transformationDatasetValidator
        },
        reference: true,
        type: Dataset.MODEL
    })
    public sourceDataset: Dataset;

    @Metamodel.attribute({
        display: stringResources.SourceLinkedService,
        constraint: {
            validator: sourceSinkLinkedServiceValidator
        },
        reference: true,
        type: LinkedService.MODEL
    })
    public sourceLinkedService: LinkedService;

    /**
     * Optional Fabric DMTS credential connection paired with `sourceLinkedService` when the source is a Fabric
     * artifact (e.g., a Lakehouse). Mirrors `Dataset.dmtsCredentialForArtifact` but at the source-transformation
     * level for inline (no-dataset) MDF sources where there is no `Dataset` to host the credential reference.
     * Not persisted by the dataflow DSL adapter today; used as a transient binding so the trident-connection-select
     * component can correctly group/render the credential connection alongside the artifact picker.
     */
    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public sourceDmtsCredentialLinkedService?: LinkedService;

    /**
     * MDF: Tracks the selected connection type (LakehouseFiles, LakehouseTables, Warehouse) for validation.
     * Used by mdfConnectionTypeValidator to show appropriate error messages.
     */
    @Metamodel.attribute({
        constraint: {
            validator: mdfConnectionTypeValidator
        }
    })
    public mdfConnectionType?: DatasetFormatType;

    @Metamodel.attribute({
        display: stringResources.LinkedService,
        reference: true,
        type: LinkedService.MODEL
    })
    public schemaLinkedService: LinkedService;

    public dataTypeSettings: IDataFlowDefaultTypeSettings = {};

    public completionAction: CompletionAction = CompletionAction.NoAction;
    public sampling: boolean = false;
    public useQuery: boolean = false;
    public documentForm = this.createTemplatableExpression(StringDataFlowType.TYPE, DocumentForm.documentPerLine);
    public unquotedColumnNames: boolean = false;
    public hasComments: boolean = false;
    public singleQuoted: boolean = false;
    public backslashEscape: boolean = false;
    public isInlineDataset: boolean = false;
    /**
     * Dataset-specific settings (format, schema, table, etc.) for inline datasets.
     * Uses validateDatasetSettings which includes:
     * - MDF format validation (via mdfFormatValidator, same as sink)
     */
    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateDatasetSettings
        }
    })
    public datasetSettings: DataFlowDatasetSettings;
    public isWorkspaceDatabase: boolean = false;
    public namespaces: boolean = true;
    public namespacePrefixes: Array<IComplexKeyValPair<string, string>> = [];
    public nullValue: string;
    public skipDatasetValidation: boolean = false;

    public workspaceDatabaseType = this.createTemplatableExpression(StringDataFlowType.TYPE, 'spark');
    @Metamodel.attribute({
        constraint: {
            validator: workspaceDatabaseValidator
        }
    })
    public workspaceDatabase: DataFlowExpression = this.createTemplatableExpression();
    @Metamodel.attribute({
        constraint: {
            validator: workspaceTableValidator
        }
    })
    public workspaceTable: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateValidationMode
        }
    })
    public validationMode: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'none');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateRowLimit
        }
    })
    public rowsLimit: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '100');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateBatchSize
        }
    })
    public batchSize: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateAllowSchemaDrift
        }
    })
    public allowSchemaDrift: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateUseSchema
        }
    })
    public useSchema: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateInferDriftedColumnTypes
        }
    })
    public inferDriftedColumnTypes: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateFileList
        }
    })
    public fileList: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: (owner: DataFlowSource, value: DataFlowExpression): IViolation[] => {
                return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourceSettingsModifiedAfter);
            }
        }
    })
    public modifiedAfter: DataFlowExpression = this.createTemplatableExpression(TimeStampDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: (owner: DataFlowSource, value: DataFlowExpression): IViolation[] => {
                return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourceSettingsModifiedBefore);
            }
        }
    })
    public modifiedBefore: DataFlowExpression = this.createTemplatableExpression(TimeStampDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateSchema
        }
    })
    public validateSchema: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.multiLineRow
        }
    })
    public multiLineRow: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({})
    public maxColumns: DataFlowExpression = this.createTemplatableExpression(NumberDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateSkipLineCount
        }
    })
    public skipLineCount: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateIsolationLevel
        }
    })
    public isolationLevel: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, IsolationLevel.ReadUncommitted);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateQuery
        }
    })
    public query: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
    })
    public format: string = SQLSourceOptions.Table;

    @Metamodel.attribute({
    })
    public schemaName: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
    })
    public storedProcInputs: DataFlowParameterConfig[] = [];

    @Metamodel.attribute({
    })
    public storedProcResultSet: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');

    @Metamodel.attribute({
    })
    public storedProcName: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateRowUrl
        }
    })
    public rowUrl: DataFlowExpression = this.createTemplatableExpression();

    public consistencyLevel: string = 'one';

    // max connection count
    public useQueryPartition: boolean = false;

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validatePartitionColumn
        }
    })
    public partitionColumn: ColumnReference = new ColumnReference(new DataFlowColumn());

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validatePartitionPredicates
        }
    })
    public partitionPredicates: DataFlowExpression[] = [];

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateMaxRecordsPerPartition
        }
    })
    public maxRecordsPerPartition: DataFlowExpression = this.createTemplatableExpression(LongDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateSapOdpPartitionPredicates
        }
    })
    public sapOdpPartitionPredicates: DataFlowExpressionArray<SapOdpPartitionPredicate[]> = this.createTemplatableExpressionArray([]);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validatePartitionRootPath
        }
    })
    public partitionRootPath: DataFlowExpression = this.createTemplatableExpression();

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateWildcardPaths
        }
    })
    public wildcardPaths: DataFlowExpression[] = [this.createTemplatableExpression()];

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateMoveFileList
        }
    })
    public moveFileList: DataFlowExpression[] = [this.createTemplatableExpression(), this.createTemplatableExpression()];

    // Cosmos settings, To be refactored for each connector
    @Metamodel.attribute({
    })
    public systemColumns: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE);

    @Metamodel.attribute({
    })
    public preferredRegions: string[] = [];

    @Metamodel.attribute({
        constraint: {
            validator: (entity: DataFlowSource, value: DataFlowExpression): IViolation[] | undefined => {
                if (!stringIsNullOrEmpty(value.expression)) {
                    const val = parseInt(value.expression, 10);
                    if (val < 400 || val > 1000000 || val % 100 !== 0) {
                        return [{
                            message: () => stringResources.ThroughputErrorMessage
                        }];
                    }
                }
                return undefined;
            }
        }
    })
    public throughput: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateIgnoreNoFilesFound
        }
    })
    public ignoreNoFilesFound: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    // for ADX
    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateTimeout
        }
    })
    public timeout: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE);

    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateChangeFeed
        }
    })
    public enableChangeFeed = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({})
    public enableCdc = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    // for Sap ODP
    @Metamodel.attribute({
        display: stringResources.DataFlowDatasetKeyColumns,
        constraint: {
            validator: DataFlowSource.validateSapOdpKeyColumns
        }
    })
    public sapOdpKeyColumns: DataFlowExpressionArray = this.createTemplatableExpressionArray();

    public changeFeedStartFromBeginning = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
    public enableSkipInitialLoad = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
    public waterMarkColumn = this.createTemplatableExpression(StringDataFlowType.TYPE);
    public enableNativeCdc = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
    public netChanges = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    public requestMethod: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE, 'GET');
    public requestBody: DataFlowExpression = this.createTemplatableExpression();
    public requestTimeout: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '30');
    public requestInterval: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '0');
    public additionalHeaders: DataFlowParameterConfig[] = [];
    public paginationRules: DataFlowParameterConfig[] = [new DataFlowParameterConfig(this.createTemplatableExpression(StringDataFlowType.TYPE, 'supportRFC5988'),
        this.createTemplatableExpression(StringDataFlowType.TYPE, 'true'))];
    public queryParameters: DataFlowParameterConfig[] = [];
    public restDataFormat: 'xml' | 'json' = 'json';
    @Metamodel.attribute({
        constraint: {
            validator: DataFlowSource.validateSapCdcReadMode
        }
    })
    public sapReadMode = this.createTemplatableExpression(StringDataFlowType.TYPE, ReadMode.FULL_AND_INCREMENTAL_LOAD);
    // for Sap ODP / SQL CDC import projection
    public executeSchemaQuery: boolean = false;

    public sapOdpLegacyMode: boolean = false;
    public fixDecimalPrecision: boolean = true;

    // for Sap ODP
    public get sapRunMode(): ReadBehavior {
        switch (this.sapReadMode.expression) {
            case ReadMode.FULL_LOAD:
                return ReadBehavior.FULL_ON_EVERY_RUN;
            case ReadMode.FULL_AND_INCREMENTAL_LOAD:
                return ReadBehavior.FULL_ON_FIRST_RUN_THEN_INCREMENTAL;
            case ReadMode.INCREMENTAL_LOAD:
                return ReadBehavior.INCREMENTAL_CHANGES_ONLY;
            default:
                return undefined;
        }
    }

    public get staged(): boolean {
        return this._staged;
    }
    public set staged(val: boolean) {
        if (val) {
            if (this.sourceDataset instanceof AzureSqlDWTableDataset && this.partitionOption === 'Partitioning' && this.partitionMethod === 'external') {
                this.partitionOption = 'Default';
            }
        }
        this._staged = val;
    }
    // max connection count

    public readMode?: 'Polybase' | 'Jdbc';

    public get owner(): DataFlow {
        return (super.getOwner() as DataFlow);
    }

    public get rowUrlColumn(): DataFlowColumn {
        if (this.rowUrl.expression && !this.rowUrl.templateExpression) {
            if (!this._rowUrlColumn) {
                const rowUrlCol = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);
                rowUrlCol.name = `${this.rowUrl.expression}`;
                rowUrlCol.dataFlowColumnType = new StringDataFlowType();
                this._rowUrlColumn = rowUrlCol;
            } else {
                this._rowUrlColumn.name = this.rowUrl.expression;
            }
        } else {
            this._rowUrlColumn = undefined;
        }
        return this._rowUrlColumn;
    }

    public get includeSchemaLinkedService(): boolean {
        return this.datasetSettings.includeSchemaLinkedServiceInResource;
    }

    private _staged: boolean = false;

    private _rowUrlColumn: DataFlowColumn;
    private _systemColumns: DataFlowColumn[] = cosmosSystemColumns.filter((col) => !col.name.startsWith('_')).map((col) => {
        const newCol = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);
        newCol.name = col.name;
        newCol.columnType = col.type;
        return newCol;
    });

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public static validateDataFlowSource(source: DataFlowSource): IViolation[] {
        const violations: IViolation[] = [];
        if (isSourceTypeEq(adfModelTypeMap.oracle.linkedServiceType, adfModelTypeMap.oracle.datasetType)(source)) {
            const linkedService = source.isInlineDataset ? source.sourceLinkedService : source.sourceDataset?.linkedService;
            if (!(linkedService?.integrationRuntime instanceof SelfHostedIntegrationRuntime)) {
                const attribute = source.isInlineDataset
                    ? DataFlowSource.MODEL.attribute('sourceLinkedService')
                    : DataFlowSource.MODEL.attribute('sourceDataset');
                violations.push({
                    message: () => stringResources.Dataflow_Oracle_IRError,
                    redirectTo: {
                        entity: source,
                        attribute
                    }
                });
            }
        }
        return violations;
    }

    public static validateBatchSize(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        if (owner.sourceDataset instanceof AzureCosmosDbV2Dataset && !stringIsNullOrEmpty(value.expression)) {
            const val = parseInt(value.expression, 10);
            if (isNaN(val) || (val < -1)) {
                return [{
                    message: () => stringResources.PageSizeErrorMessage
                }];
            }
        }
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.ModelName_BatchSize);
    }

    public static async validateQuery(owner: DataFlowSource, value: DataFlowExpression, violation: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];
        const dsRegistrySvc = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        if (owner.isQueryMode({ dsRegistrySvc }) && !owner.query.expression) {
            violations.push({
                context: owner,
                message: () => stringResources.DataFlowSourcePanelEmptyQuery
            });
        }
        violations.push(...Transformation.validateTemplateExpressions(owner, [value], stringResources.QueryLabel));
        return violations;
    }

    public static validateIsolationLevel(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSQLIsolationLevel);
    }

    public static validateTimeout(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.Timeout);
    }

    public static validateChangeFeed(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        if (!owner.useQuery && value.expression === 'true' && owner.allowSchemaDrift.expression === 'true' && owner.inferDriftedColumnTypes.expression === 'true') {
            return [
                {
                    context: owner,
                    message: () => stringResources.DataFlowCosmosChangeFeedError
                }
            ];
        }
        return undefined;
    }

    public static validateSapCdcReadMode(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        if (!owner.sapOdpLegacyMode) {
            const violations: IViolation[] = [];
            if (
                !owner.isInlineDataset &&
                !value.templateExpression &&
                value.expression !== ReadMode.FULL_LOAD &&
                owner.sourceLinkedService instanceof SapOdpLinkedService &&
                !owner.sourceLinkedService.fullyByJsonEditor &&
                propertyIsNullOrEmpty(owner.sourceLinkedService, SapOdpLinkedService.SUBSCRIBER_NAME)
            ) {
                violations.push({
                    message: () =>
                        format(
                            stringResources.Validation_SapCDCSubscriberNameRequired,
                            owner.sourceLinkedService.name,
                            value.expression === ReadMode.FULL_AND_INCREMENTAL_LOAD
                                ? stringResources.DataFlowFullOnFirstRunThenIncremental
                                : stringResources.DataFlowIncrementalChangesOnly
                        ),
                    redirectTo: {
                        entity: owner.sourceLinkedService,
                        attribute: SapOdpLinkedService.SUBSCRIBER_NAME
                    }
                });
            }
            return violations;
        }
        return undefined;
    }

    public static validateSapOdpKeyColumns(owner: DataFlowSource, value: DataFlowExpressionArray): IViolation[] {
        const violations: IViolation[] = [];
        if (
            !owner.isInlineDataset
            && owner.sourceDataset instanceof SapOdpDataset
            && !value?.templateExpression
            && !value?.expressions.length
            && !owner.sapReadMode.templateExpression
            && owner.sapReadMode.expression !== ReadMode.FULL_LOAD
        ) {
            violations.push({
                message: () => format(stringResources.RequiredTemplate, stringResources.DataFlowDatasetKeyColumns)
            });
        }
        return violations;
    }

    /**
     * Validates datasetSettings for source.
     * Includes MDF format validation (via mdfFormatValidator, same as sink).
     */
    private static validateDatasetSettings(dataFlowSource: DataFlowSource, value: DataFlowDatasetSettings): IViolation[] {
        // MDF format validation - same validator as sink for consistent behavior
        return mdfFormatValidator(dataFlowSource, value) ?? [];
    }

    private static validateRowLimit(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelRowsLimit);
    }

    private static validateValidationMode(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        if ((owner.isInlineDataset && owner.datasetSettings && owner.datasetSettings.getFormat() === DatasetFormatType.Xml)
            || (!owner.isInlineDataset && owner.sourceDataset && owner.sourceDataset instanceof XmlDataset)) {
            if (value.expression === 'xsd' && !owner.namespaces) {
                return [{
                    context: owner,
                    message: () => stringResources.DataflowXsdShouldWorkWithNamespace
                }];
            }
        }
        return undefined;
    }

    private static validateAllowSchemaDrift(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelAllowSchemaDrift);
    }

    private static validateUseSchema(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        const violations: IViolation[] = [];
        if (isTruthy(value)) {
            if (isTruthy(owner.allowSchemaDrift) || isTruthy(owner.validateSchema) || isTruthy(owner.inferDriftedColumnTypes)) {
                violations.push({
                    context: owner,
                    message: () => stringResources.DataFlowSourceUseSchemaViolation
                });
            }
            if (owner.schema.length === 0) {
                violations.push({
                    context: owner,
                    message: () => stringResources.DataFlowSourceUseSchemaEmptyViolation
                });
            }
        }
        violations.push(...Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelUseProjectedSchema));
        return violations;
    }

    private static validateInferDriftedColumnTypes(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelInferDriftedColumnTypes);
    }

    private static validateFileList(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourceSettingsListOfFiles);
    }

    private static validateSchema(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelValidateSchema);
    }

    private static multiLineRow(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourcePanelValidateSchema);
    }

    private static validateSkipLineCount(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.SkipLineCountLabel);
    }

    private static validateIgnoreNoFilesFound(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlowSourceSettingsAllowNoFilesFound);
    }

    private static validateRowUrl(owner: DataFlowSource, value: DataFlowExpression): IViolation[] | undefined {
        const violations: IViolation[] = [];
        // Only columns from dataset should handle this
        if (value.expression && !owner.columns.every((column) => value.expression !== column.name)) {
            violations.push({
                context: owner,
                message: () => sanitizeAndFormatString(stringResources.DataFlowErrorDuplicateColumn_RowUrl, value.expression)
            });
        }
        violations.push(...Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlow_RowUrl));
        return violations;
    }

    private static validatePartitionColumn(owner: DataFlowSource, value: ColumnReference): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (owner.sourceDataset instanceof AzureSqlTableDataset && owner.partitionMethod === 'external' && !owner.useQueryPartition) {
            if (!value || stringIsNullOrEmpty(value.dynamicPartitionColumn?.templateExpression) && !owner.outputColumns().find((col) => col.name === value.column?.name)) {
                violations.push({
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, DataFlowSource.MODEL.name, owner.name, stringResources.DataFlow_PartitionColumn_Label)
                });
            }
        }
        // If we do not have an expression, check the case of external partitioning when there are not query conditions.
        if (!owner.numberOfPartitions.templateExpression && owner.partitionOption === 'Partitioning' && owner.partitionMethod === 'external' && !owner.useQueryPartition) {
            const numberOfPartitions = parseInt(owner.numberOfPartitions.expression, 10);
            if (isNaN(numberOfPartitions) || numberOfPartitions < Transformation.PARTITIONING_CONFIG_MIN_PARTITIONS) {
                violations.push({
                    context: owner,
                    message: () => sanitizeAndFormatString(stringResources.DataFlowTransformationNumberOfPartitionsError, owner.name)
                });
            }
        }
        return violations;
    }

    private static validatePartitionPredicates(owner: DataFlowSource, value: DataFlowExpression[]): IViolation[] | undefined {
        if (owner.sourceDataset instanceof AzureSqlTableDataset && owner.partitionMethod === 'external' && owner.useQueryPartition) {
            if (value == null || value.filter((v) => !!v.expression).length === 0) {
                return [
                    {
                        context: owner,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, DataFlowSource.MODEL.name, owner.name, stringResources.DataFlowPartitionConditions)
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateMaxRecordsPerPartition(owner: DataFlowSource, value: DataFlowExpression): IViolation[] | undefined {
        if (owner.datasetSettings instanceof EventHubsSettings && owner.partitionMethod === 'external') {
            if (stringIsNullOrEmpty(value?.expression)){
                return [
                    {
                        context: owner,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, DataFlowSource.MODEL.name, owner.name, stringResources.DataFlowMaxRecordsPerPartition)
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateSapOdpPartitionPredicates(owner: DataFlowSource, value: DataFlowExpressionArray<SapOdpPartitionPredicate[]>): IViolation[] | undefined {
        const violations: IViolation[] = [];
        const attribute = DataFlowSource.MODEL.attribute('sapOdpPartitionPredicates');
        if (
            value
            && !value.templateExpression
            && (
                owner.sourceDataset instanceof SapOdpDataset
                || owner.datasetSettings instanceof SapOdpSettings
            )
            && owner.partitionMethod === 'external'
            && owner.useQueryPartition
        ) {
            if (value.expressions == null || value.expressions.filter((v) => !!v.length).length === 0) {
                violations.push({
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, DataFlowSource.MODEL.name, owner.name, stringResources.DataFlowPartitionConditions),
                    redirectTo: {
                        entity: owner,
                        attribute
                    }
                });
            }
            if (Array.isArray(value.expressions)) {
                for (const predicates of value.expressions) {
                    if (Array.isArray(predicates)) {
                        for (const predicate of predicates) {
                            const { option, low } = predicate;
                            if (
                                !option.templateExpression
                                && !low.templateExpression
                                && option.expression === SapSelectionOption.CP
                                && Constants.wildcardsValidation.test(low.expression)
                            ) {
                                violations.push({
                                    message: () => stringResources.SapCdc_Copy_Selection_Option_Pattern_Error,
                                    redirectTo: {
                                        entity: owner,
                                        attribute
                                    }
                                });
                            }
                        }
                    }
                }
            }
        }
        return violations;
    }

    private static validatePartitionRootPath(owner: DataFlowSource, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.DataFlow_PartitionRootPath);
    }

    private static validateWildcardPaths(owner: DataFlowSource, value: DataFlowExpression[]): IViolation[] {
        const violations: IViolation[] = [];
        violations.push(...Transformation.validateTemplateExpressions(owner, value, stringResources.DataFlowWildcardPaths));
        return violations;
    }

    private static validateMoveFileList(owner: DataFlowSource, value: DataFlowExpression[]): IViolation[] {
        const violations: IViolation[] = [];
        if (owner.completionAction === CompletionAction.MoveFile && !owner.moveFileList[1].expression) {
            violations.push({
                context: owner,
                message: () => stringResources.DataFlowSourceErrorMoveToEmpty
            });
        }
        violations.push(...Transformation.validateTemplateExpressions(owner, value, stringResources.DataFlowMoveFiles));
        return violations;
    }

    public get isDelimitedWithoutHeader(): boolean {
        return (this.sourceDataset instanceof DelimitedTextDataset || this.sourceDataset instanceof ExcelDataset) &&
            !this.sourceDataset.firstRowAsHeader &&
            (this.sourceDataset.columns.length === 0 || stringIsNullOrEmpty(this.sourceDataset.columns[0].name));
    }

    protected get inputColumns(): DataFlowColumn[] {
        return [];
    }

    public get datasetColumns(): DataFlowColumn[] {
        if (this.sourceDataset) {
            return DataFlowTypeConvertor.convertDatasetColumnsToDataFlowColumns(this.sourceDataset, this, /** skip logging in getter */undefined);
        }
        return [];
    }

    public getExpressions(): DataFlowExpression[] {
        return this.getAllExpressionsForModel();
    }

    public clear(): void {
        super.clear();
    }

    public isQueryMode(services: { dsRegistrySvc: DatasetRegistryService }): boolean {
        const sourceDatasetSupportsStoreProcedure = !!this.sourceDataset &&
            !!services.dsRegistrySvc.getDatasetConfiguration(Metamodel.modelOf(this.sourceDataset).name).supportDataFlowStoreProcedure;
        return !sourceDatasetSupportsStoreProcedure ? !!this.useQuery : this.format === 'query';
    }

    public get parameters(): OwnedSet<Parameter> {
        return this.sourceDataset && this.sourceDataset.parameters;
    }

    /**
     * Initialize dataflow source default setting, called after changing dataset.
     * @param datasetType
     */
    public initialize(datasetType: string): void {
        if (datasetType === DatasetFormatType.CDM) {
            this.multiLineRow.expression = 'true';  // For CDM source, multiLineRow is true by default
        }
    }

    public bypassSchemaValidation = () => (isTruthy(this.allowSchemaDrift));

    public isSchemaFromDataset(): boolean {
        return this.sourceDataset.isV2 && !(this.sourceDataset instanceof DelimitedTextDataset);
    }

    public importSchemaFromDataset(): void {
        if (!this.useQuery) {
            this.schema.length = 0;
            this.datasetColumns.forEach((column) => {
                this.schema.push(column);
            });
        }
    }

    public shallowImportSchemaFromDataset(hardTypedDataset: boolean): void {
        if (hardTypedDataset) {
            this.importSchemaFromDataset();
        } else if (!this.useQuery) {
            const originalSourceSchema = this.schema;
            const newSchemaList: DataFlowColumn[] = [];
            this.datasetColumns.forEach((newDatasetCol) => {
                const oldSourceColumn = originalSourceSchema.find((col) => col.name === newDatasetCol.name);
                newSchemaList.push(oldSourceColumn || newDatasetCol);
            });
            this.schema.length = 0;
            this.schema.push(...newSchemaList);
        }
    }

    public importSchemaFromDelimitedNoHeaderDataset(): void {
        if (this.isDelimitedWithoutHeader) {
            const datasetColumns = this.datasetColumns;
            if (datasetColumns.length > this.schema.length) {
                this.schema.push(...datasetColumns.slice(this.schema.length));
            } else if (datasetColumns.length < this.schema.length) {
                this.schema.length = datasetColumns.length;
            }
        }
    }

    public updateTransformationCache(): void {
        super.updateTransformationCache();
        if (this.isInlineDataset && this.datasetSettings != null) {
            // Update datasetSettings cache
            const dataFlowArguments = getDataFlowBaseArguments(this.dataflow);
            updateDatasetSettingsCache(this.datasetSettings, dataFlowArguments);
        }
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
    }

    protected getGeneratedDescription(): string {
        if (this.sourceDataset) {
            return format(stringResources.SourceDescription, this.sourceDataset.name);
        }
        if (this.sourceLinkedService) {
            return format(stringResources.SourceDescription, this.sourceLinkedService.name);
        }
        return stringResources.SourceNoDatasetDescription;
    }

    protected _outputColumns(): DataFlowColumn[] {
        let result = this.columns.filter((column) => column.name);
        if (this.rowUrlColumn != null && result.findIndex((column) => column.name === this.rowUrlColumn.name) < 0) {
            result = result.concat(this.rowUrlColumn);
        }

        if (!this.useQuery) {
            // add system schema for cosmos DB
            if (this.sourceDataset instanceof AzureCosmosDbV2Dataset && this.systemColumns.expression != null && this.systemColumns.expression.toString() === 'true') {
                result = result.concat(this._systemColumns);
            }
        }

        return result;
    }
}
